import * as React from 'react';
import { Vector3, Group, Object3D, OrthographicCamera, Mesh } from 'three';
import { Assign } from 'utility-types';
import { ThreeElements, useFrame, useThree } from '@react-three/fiber';
import { CalculatePosition, ForwardRefComponent, PointerEvents } from './types';
import { isRefObject } from './react';

import {
  calculatePosition as defaultCalculatePosition,
  epsilon,
  getCameraCSSMatrix,
  getObjectCSSMatrix,
  isObjectBehindCamera,
  isObjectVisible,
  objectScale,
  objectZIndex,
} from './utils';
import { OcclusionMesh } from './OcclusionMesh';
import { useHtmlMount } from './useHtmlMount';

export interface HtmlProps
  extends Omit<Assign<React.HTMLAttributes<HTMLDivElement>, ThreeElements['group']>, 'ref'> {
  prepend?: boolean;
  center?: boolean;
  fullscreen?: boolean;
  eps?: number;
  portal?: React.RefObject<HTMLElement>;
  distanceFactor?: number;
  sprite?: boolean;
  transform?: boolean;
  zIndexRange?: [number, number];
  calculatePosition?: CalculatePosition;
  as?: string;
  pointerEvents?: PointerEvents;
  // Occlusion based off work by Jerome Etienne and James Baicoianu
  // https://www.youtube.com/watch?v=ScZcUEDGjJI
  // as well as Joe Pea in CodePen: https://codepen.io/trusktr/pen/RjzKJx
  occlude?: React.RefObject<Object3D>[] | boolean | 'raycast' | 'blending';
  onOcclude?: (hidden: boolean) => void;
  material?: React.ReactNode; // Material for occlusion plane
  geometry?: React.ReactNode; // Material for occlusion plane
  castShadow?: boolean; // Cast shadow for occlusion plane
  receiveShadow?: boolean; // Receive shadow for occlusion plane
  wrapperRef?: React.Ref<HTMLElement>; // Ref for the wrapper element
  wrapperTransform?: false; // If false, no wrapper transform is applied on render phase
  wrapperClass?: string;
}

export const Html: ForwardRefComponent<HtmlProps, HTMLDivElement> =
  /* @__PURE__ */ React.forwardRef(
    (
      {
        children,
        eps = 0.001,
        style,
        className,
        prepend,
        center,
        fullscreen,
        portal,
        distanceFactor,
        sprite = false,
        transform = false,
        occlude,
        onOcclude,
        castShadow,
        receiveShadow,
        material,
        geometry,
        zIndexRange = [16777271, 0] as [number, number],
        calculatePosition = defaultCalculatePosition,
        as = 'div',
        pointerEvents = 'auto',
        wrapperClass,
        wrapperRef,
        wrapperTransform: wrapperStyle,
        ...props
      }: HtmlProps,
      fowardedRef: React.Ref<HTMLDivElement>,
    ) => {
      const { gl, camera, scene, size, raycaster, viewport } = useThree();

      const group = React.useRef<Group>(null!);
      const oldZoom = React.useRef(0);
      const oldPosition = React.useRef([0, 0]);
      const transformOuterRef = React.useRef<HTMLDivElement>(null!);
      const transformInnerRef = React.useRef<HTMLDivElement>(null!);

      const occlusionMeshRef = React.useRef<Mesh>(null!);
      const isMeshSizeSet = React.useRef<boolean>(false);

      const isRayCastOcclusion = React.useMemo(() => {
        return (
          (occlude && occlude !== 'blending') ||
          (Array.isArray(occlude) && occlude.length && isRefObject(occlude[0]))
        );
      }, [occlude]);

      const { el, root, target } = useHtmlMount(as, portal, prepend, !!group.current);

      React.useImperativeHandle(wrapperRef, () => el);

      React.useLayoutEffect(() => {
        const el = gl.domElement as HTMLCanvasElement;

        if (occlude && occlude === 'blending') {
          el.style.zIndex = `${Math.floor(zIndexRange[0] / 2)}`;
          el.style.position = 'absolute';
          el.style.pointerEvents = 'none';
        } else {
          el.style.zIndex = null!;
          el.style.position = null!;
          el.style.pointerEvents = null!;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [occlude]);

      React.useLayoutEffect(() => {
        if (group.current) {
          scene.updateMatrixWorld();
          if (transform) {
            el.style.cssText =
              'position:absolute;top:0;left:0;pointer-events:none;overflow:hidden;';
          } else {
            const vec = calculatePosition(group.current, camera, size);
            el.style.cssText = `position:absolute;top:0;left:0;transform:translate3d(${vec[0]}px,${vec[1]}px,0);transform-origin:0 0;`;
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [target, transform]);

      React.useLayoutEffect(() => {
        if (wrapperClass) el.className = wrapperClass;
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [wrapperClass]);

      const styles: React.CSSProperties = React.useMemo(() => {
        if (transform) {
          return {
            position: 'absolute',
            top: 0,
            left: 0,
            width: size.width,
            height: size.height,
            transformStyle: 'preserve-3d',
            pointerEvents: 'none',
          };
        } else {
          return {
            position: 'absolute',
            transform: center ? 'translate3d(-50%,-50%,0)' : 'none',
            ...(fullscreen && {
              top: -size.height / 2,
              left: -size.width / 2,
              width: size.width,
              height: size.height,
            }),
            ...style,
          };
        }
      }, [style, center, fullscreen, size, transform]);

      const transformInnerStyles: React.CSSProperties = React.useMemo(
        () => ({ position: 'absolute', pointerEvents }),
        [pointerEvents],
      );

      React.useLayoutEffect(() => {
        isMeshSizeSet.current = false;

        if (transform) {
          root.current?.render(
            <div ref={transformOuterRef} style={styles}>
              <div ref={transformInnerRef} style={transformInnerStyles}>
                <div ref={fowardedRef} className={className} style={style} children={children} />
              </div>
            </div>,
          );
        } else {
          root.current?.render(
            <div ref={fowardedRef} style={styles} className={className} children={children} />,
          );
        }
      });

      const visible = React.useRef(true);

      useFrame((gl) => {
        if (group.current) {
          camera.updateMatrixWorld();
          group.current.updateWorldMatrix(true, false);
          const vec = transform
            ? oldPosition.current
            : calculatePosition(group.current, camera, size);

          if (
            transform ||
            Math.abs(oldZoom.current - camera.zoom) > eps ||
            Math.abs(oldPosition.current[0] - vec[0]) > eps ||
            Math.abs(oldPosition.current[1] - vec[1]) > eps
          ) {
            const isBehindCamera = isObjectBehindCamera(group.current, camera);
            let raytraceTarget: null | undefined | boolean | Object3D[] = false;

            if (isRayCastOcclusion) {
              if (Array.isArray(occlude)) {
                raytraceTarget = occlude.map((item) => item.current) as Object3D[];
              } else if (occlude !== 'blending') {
                raytraceTarget = [scene];
              }
            }

            const previouslyVisible = visible.current;
            if (raytraceTarget) {
              const isvisible = isObjectVisible(group.current, camera, raycaster, raytraceTarget);
              visible.current = isvisible && !isBehindCamera;
            } else {
              visible.current = !isBehindCamera;
            }

            if (previouslyVisible !== visible.current) {
              if (onOcclude) onOcclude(!visible.current);
              else el.style.display = visible.current ? 'block' : 'none';
            }
            if (wrapperStyle !== false) {
              const halfRange = Math.floor(zIndexRange[0] / 2);
              const zRange: [number, number] = occlude
                ? isRayCastOcclusion //
                  ? [zIndexRange[0], halfRange]
                  : [halfRange - 1, 0]
                : zIndexRange;

              el.style.zIndex = `${objectZIndex(group.current, camera, zRange)}`;
            }

            if (transform) {
              const [widthHalf, heightHalf] = [size.width / 2, size.height / 2];
              const fov = camera.projectionMatrix.elements[5] * heightHalf;
              const { isOrthographicCamera, top, left, bottom, right } =
                camera as OrthographicCamera;
              const cameraMatrix = getCameraCSSMatrix(camera.matrixWorldInverse);
              const cameraTransform = isOrthographicCamera
                ? `scale(${fov})translate(${epsilon(-(right + left) / 2)}px,${epsilon((top + bottom) / 2)}px)`
                : `translateZ(${fov}px)`;
              const matrix = group.current.matrixWorld;
              if (sprite) {
                /*matrix = camera.matrixWorldInverse
                  .clone()
                  .transpose()
                  .copyPosition(matrix)
                  .scale(group.current.scale);
                matrix.elements[3] = matrix.elements[7] = matrix.elements[11] = 0;
                matrix.elements[15] = 1;*/
                group.current.lookAt(gl.camera.position);
              }
              el.style.width = size.width + 'px';
              el.style.height = size.height + 'px';
              el.style.perspective = isOrthographicCamera ? '' : `${fov}px`;
              if (transformOuterRef.current && transformInnerRef.current) {
                transformOuterRef.current.style.transform = `${cameraTransform}${cameraMatrix}translate(${widthHalf}px,${heightHalf}px)`;
                transformInnerRef.current.style.transform =
                  'translate(-50%,-50%)' +
                  getObjectCSSMatrix(matrix, 1 / ((distanceFactor || 10) / 400));
              }
            } else {
              if (wrapperStyle !== false) {
                const scale =
                  distanceFactor === undefined
                    ? 1
                    : objectScale(group.current, camera) * distanceFactor;
                el.style.transform = `translate3d(${vec[0]}px,${vec[1]}px,0) scale(${scale})`;
              }
            }
            oldPosition.current = vec;
            oldZoom.current = camera.zoom;
          }
        }

        if (!isRayCastOcclusion && occlusionMeshRef.current && !isMeshSizeSet.current) {
          if (transform) {
            if (transformOuterRef.current) {
              const el = transformOuterRef.current.children[0];

              if (el?.clientWidth && el?.clientHeight) {
                const { isOrthographicCamera } = camera as OrthographicCamera;

                if (isOrthographicCamera || geometry) {
                  if (props.scale) {
                    if (!Array.isArray(props.scale)) {
                      occlusionMeshRef.current.scale.setScalar(1 / (props.scale as number));
                    } else if (props.scale instanceof Vector3) {
                      occlusionMeshRef.current.scale.copy(props.scale.clone().divideScalar(1));
                    } else {
                      occlusionMeshRef.current.scale.set(
                        1 / props.scale[0],
                        1 / props.scale[1],
                        1 / props.scale[2],
                      );
                    }
                  }
                } else {
                  const ratio = (distanceFactor || 10) / 400;
                  const w = el.clientWidth * ratio;
                  const h = el.clientHeight * ratio;

                  occlusionMeshRef.current.scale.set(w, h, 1);
                }

                isMeshSizeSet.current = true;
              }
            }
          } else {
            const ele = el.children[0];

            if (ele?.clientWidth && ele?.clientHeight) {
              const ratio = 1 / viewport.factor;
              const w = ele.clientWidth * ratio;
              const h = ele.clientHeight * ratio;

              occlusionMeshRef.current.scale.set(w, h, 1);

              isMeshSizeSet.current = true;
            }

            occlusionMeshRef.current.lookAt(gl.camera.position);
          }
        }
      });

      return (
        <group {...props} ref={group}>
          {occlude && !isRayCastOcclusion && (
            <OcclusionMesh
              ref={occlusionMeshRef}
              sprite={!transform}
              anchor={center ? 'center' : 'top-left'}
              geometry={geometry}
              material={material}
              castShadow={castShadow}
              receiveShadow={receiveShadow}
            />
          )}
        </group>
      );
    },
  );
